import React, { useState, useCallback, useEffect } from 'react';
import { useToast } from '@/components/Toast/useToast';
import './AppShareModal.css';

// ===== 图标 =====
const icons: Record<string, string> = {
  close: '<line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>',
  copy: '<rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>',
  link: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
  users: '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  globe: '<circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>',
  lock: '<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
  check: '<polyline points="20 6 9 17 4 12"/>',
};

function Icon({ name, size = 24 }: { name: string; size?: number }) {
  const path = icons[name] || icons.link;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" dangerouslySetInnerHTML={{ __html: path }} />
  );
}

interface AppShareModalProps {
  app: {
    id: number | string;
    name: string;
    desc: string;
    visibility?: 'public' | 'dept';
  };
  onClose: () => void;
}

export function AppShareModal({ app, onClose }: AppShareModalProps) {
  const toast = useToast();
  const [mode, setMode] = useState<'internal' | 'public'>('internal');
  const [copied, setCopied] = useState(false);

  const internalLink = `${window.location.origin}/apps/${app.id}`;
  const publicLink = `${window.location.origin}/public/apps/${app.id}`;

  const currentLink = mode === 'internal' ? internalLink : publicLink;

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(currentLink);
      setCopied(true);
      toast.success('链接已复制');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('复制失败，请手动复制');
    }
  }, [currentLink, toast]);

  // 阻止背景滚动
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div className="modal-mask open" onClick={onClose}>
      <div className="share-dialog" onClick={e => e.stopPropagation()}>
        <button type="button" className="share-close" onClick={onClose}>
          <Icon name="close" size={18} />
        </button>

        <div className="share-header">
          <h2>分享「{app.name}」</h2>
          <p>选择分享方式，将应用链接发送给同事或外部人员</p>
        </div>

        <div className="share-modes">
          <button
            type="button"
            className={`share-mode ${mode === 'internal' ? 'active' : ''}`}
            onClick={() => setMode('internal')}
          >
            <span className="mode-icon">
              <Icon name="users" size={22} />
            </span>
            <div className="mode-info">
              <strong>内部使用</strong>
              <small>仅企业成员可访问，需要登录</small>
            </div>
            {mode === 'internal' && (
              <span className="mode-check">
                <Icon name="check" size={16} />
              </span>
            )}
          </button>

          <button
            type="button"
            className={`share-mode ${mode === 'public' ? 'active' : ''}`}
            onClick={() => setMode('public')}
            disabled={app.visibility === 'dept'}
          >
            <span className="mode-icon">
              <Icon name="globe" size={22} />
            </span>
            <div className="mode-info">
              <strong>公开外链</strong>
              <small>{app.visibility === 'dept' ? '本部门应用不支持公开分享' : '任何人可通过链接访问'}</small>
            </div>
            {mode === 'public' && (
              <span className="mode-check">
                <Icon name="check" size={16} />
              </span>
            )}
          </button>
        </div>

        <div className="share-link-section">
          <label>分享链接</label>
          <div className="share-link-row">
            <input
              type="text"
              value={currentLink}
              readOnly
              onClick={e => (e.target as HTMLInputElement).select()}
            />
            <button type="button" className={`copy-btn ${copied ? 'copied' : ''}`} onClick={handleCopy}>
              <Icon name={copied ? 'check' : 'copy'} size={16} />
              {copied ? '已复制' : '复制'}
            </button>
          </div>
          {mode === 'public' && (
            <p className="share-warning">
              <Icon name="lock" size={14} />
              公开链接可能包含敏感数据，请谨慎分享
            </p>
          )}
        </div>

        <div className="share-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>关闭</button>
        </div>
      </div>
    </div>
  );
}

export default AppShareModal;
